/* eslint-disable no-shadow */
import { Action } from '@ngrx/store';
import { Component, ComponentOutputEvent, ComponentOutputEventPartial, ComponentPairDto, ComponentPartial, Page, PagePartial, PageWithRelations, UpdateContainerPropertiesDto } from '@rappider/rappider-sdk';
import { ContentEditorClipboardData } from '../models/content-editor-clipboard-data.interface';
import { ContentTree, ContentTreeItem, ContentTreeItemType } from 'libs/content-tree-renderer/src/lib/models';
import { AddConditionToContainerRequestDto } from 'libs/rappider-sdk/src/lib/models/add-condition-to-container-request-dto';
import { UpdateContainerConditionRequestDto } from 'libs/rappider-sdk/src/lib/models/update-container-condition-request-dto';
import { ContainerRemoveStrategy } from '../models/container-remove-strategy.enum';

export enum ActionTypes {
  ContentEditorError = '[ContentEditor] ContentEditorError',
  LoadPageWithComponents = '[ContentEditor] LoadPageWithComponents',
  ReloadPageContent = '[ContentEditor] ReloadPageContent',
  LoadPageWithComponentsSuccessful = '[ContentEditor] LoadPageWithComponentsSuccessful',
  CreateComponent = '[ContentEditor] CreateComponent',
  CreateComponentSuccessful = '[ContentEditor] CreateComponentSuccessful',
  UpdateComponent = '[ContentEditor] UpdateComponent',
  UpdateComponentSuccessful = '[ContentEditor] UpdateComponentSuccessful',
  DeleteComponent = '[ContentEditor] DeleteComponent',
  DeleteComponentSuccessful = '[ContentEditor] DeleteComponentSuccessful',
  UpdateComponentInputs = '[ContentEditor] UpdateComponentInputs',
  UpdateComponentInputsFailure = '[ContentEditor] UpdateComponentInputsFailure',
  SetActiveContentTreeItem = '[ContentEditor] SetActiveContentTreeItem',
  CreatePageContainer = '[ContentEditor] CreatePageContainer',
  UpdatePageContainerProperties = '[ContentEditor] UpdatePageContainerProperties',
  DeletePageContainer = '[ContentEditor] DeletePageContainer',
  MoveContentTreeItem = '[ContentEditor] MoveContentTreeItem',
  UpdatePageCss = '[ContentEditor] UpdatePageCss',
  UpdatePageCssSuccesful = '[ContentEditor] UpdatePageCssSuccesful',
  CopyPage = '[ContentEditor] CopyPage',
  CopyContainer = '[ContentEditor] CopyContainer',
  CopyContainerSuccessful = '[ContentEditor] CopyContainerSuccessful',
  CopyPageSuccessful = '[ContentEditor] CopyPageSuccessful',
  CopyTemplateToPageContainer = '[ContentEditor] CopyTemplateToPageContainer',
  CopyTemplateToPageContainerSuccessful = '[ContentEditor] CopyTemplateToPageContainerSuccessful',
  CopyActiveContentTemplateToLocalStorage = '[ContentEditor] CopyActiveContentTemplateToLocalStorage',
  WriteCopiedTemplateTreeFromLocalStorageToState = '[ContentEditor] WriteCopiedTemplateTreeFromLocalStorageToState',
  WriteCopiedTemplateTreeFromLocalStorageToStateFailure = '[ContentEditor] WriteCopiedTemplateTreeFromLocalStorageToStateFailure',
  PasteTemplateFromLocalStorage = '[ContentEditor] PasteTemplateFromLocalStorage',
  ResetContentEditorData = '[ContentEditor] ResetContentEditorData',
  UpdateContentTreeOfPage = '[ContentEditor] UpdateContentTreeOfPage',
  UpdateContentTreeOfPageSuccessful = '[ContentEditor] UpdateContentTreeOfPageSuccessful',
  UpdateContentTreeAsIs = '[ContentEditor] UpdateContentTreeAsIs',
  UpdateContentTreeAsIsSuccessful = '[ContentEditor] UpdateContentTreeAsIsSuccessful',
  CreateContainerVisibilityCondition = '[ContentEditor] CreateContainerVisibilityCondition',
  CreateContainerVisibilityConditionSuccessful = '[ContentEditor] CreateContainerVisibilityConditionSuccessful',
  UpdateContainerVisibilityCondition = '[ContentEditor] UpdateContainerVisibilityCondition',
  UpdateContainerVisibilityConditionSuccessful = '[ContentEditor] UpdateContainerVisibilityConditionSuccessful',
  DeleteContainerVisibilityCondition = '[ContentEditor] DeleteContainerVisibilityCondition',
  DeleteContainerVisibilityConditionSuccessful = '[ContentEditor] DeleteContainerVisibilityConditionSuccessful',
  CreateComponentOutputEvent = '[ContentEditor] CreateComponentOutputEvent',
  CreateComponentOutputEventSuccessful = '[ContentEditor] CreateComponentOutputEventSuccessful',
  UpdateComponentOutputEvent = '[ContentEditor] UpdateComponentOutputEvent',
  UpdateComponentOutputEventSuccessful = '[ContentEditor] UpdateComponentOutputEventSuccessful',
  DeleteComponentOutputEvent = '[ContentEditor] DeleteComponentOutputEvent',
  DeleteComponentOutputEventSuccessful = '[ContentEditor] DeleteComponentOutputEventSuccessful',
  SyncPageDataOptimistic = '[ContentEditor] SyncPageDataOptimistic',
  ContentEditorDestroy = '[ContentEditor] ContentEditorDestroy',
  DeletePageComponents = '[ContentEditor] DeletePageComponents',
  DeletePageComponentsSuccessful = '[ContentEditor] DeletePageComponentsSuccessful',
  GetTemplateById = '[ContentEditor] GetTemplateById',
  GetTemplateByIdSuccessful = '[ContentEditor] GetTemplateByIdSuccessful',
  AddComponentWithPosition = '[ContentEditor] AddComponentWithPosition',
  GenerateSubscriptionsAndValuesByAi = '[ContentEditor] GenerateSubscriptionsAndValuesByAi',
  GenerateSubscriptionsAndValuesByAiSuccessful = '[ContentEditor] GenerateSubscriptionsAndValuesByAiSuccessful',
  GenerateComponentIntoPageContainer = '[ContentEditor] GenerateComponentIntoPageContainer',
  GenerateComponentIntoPageContainerComponentGenerated = '[ContentEditor] GenerateComponentIntoPageContainerComponentGenerated',
  GenerateComponentIntoPageContainerSuccessful = '[ContentEditor] GenerateComponentIntoPageContainerSuccessful',
  VerifyStatusOfAIGeneratedContainers = '[ContentEditor] VerifyStatusOfAIGeneratedContainers',
  CopyPageContentTree = '[ContentEditor] CopyPageContentTree',
  CopyPageContentTreeSuccessful = '[ContentEditor] CopyPageContentTreeSuccessful',
  GetPageByIdAndReplaceSuccessful = '[ContentEditor] GetPageByIdAndReplaceSuccessful',
}

export class ContentEditorError implements Action {
  readonly type = ActionTypes.ContentEditorError;
  constructor(public payload: { errorOn: string; error?: any }) { }
}

export class LoadPageWithComponents implements Action {
  readonly type = ActionTypes.LoadPageWithComponents;
  constructor(public payload: { pageId: string }) { }
}

export class ReloadPageContent implements Action {
  readonly type = ActionTypes.ReloadPageContent;
  constructor(public payload: { pageId: string }) { }
}

export class LoadPageWithComponentsSuccessful implements Action {
  readonly type = ActionTypes.LoadPageWithComponentsSuccessful;
  constructor(public payload: { page: PageWithRelations }) { }
}

export class CreateComponent implements Action {
  readonly type = ActionTypes.CreateComponent;
  constructor(public payload: {
    componentDefinitionId: string;
    title?: string;
    inputs?: any;
  }) { }
}

export class CreateComponentSuccessful implements Action {
  readonly type = ActionTypes.CreateComponentSuccessful;
  constructor(public payload: {
    createdComponent: { component: Component; componentId: string; id: string; type: ContentTreeItemType };
    pageContainerId: string;
    // the pageComponentId where the component will be related with the Page
    pageComponentId?: string;
  }) { }
}

export class UpdateComponent implements Action {
  readonly type = ActionTypes.UpdateComponent;
  constructor(
    public payload: {
      component: ComponentPartial;
    }
  ) { }
}

export class UpdateComponentSuccessful implements Action {
  readonly type = ActionTypes.UpdateComponentSuccessful;
  constructor(
    public payload: {
      component: Component;
    }
  ) { }
}

export class DeleteComponent implements Action {
  readonly type = ActionTypes.DeleteComponent;
  constructor(public payload: { componentId: string }) { }
}

export class DeleteComponentSuccessful implements Action {
  readonly type = ActionTypes.DeleteComponentSuccessful;
  constructor(public payload: { deletedComponentId: string }) { }
}

export class UpdateComponentInputs implements Action {
  readonly type = ActionTypes.UpdateComponentInputs;
  constructor(
    public payload: {
      componentId: string;
      parentPageComponentContentId: string;
      inputs: { [key: string]: any };
      pageType?: string;
    }
  ) { }
}

export class SetActiveContentTreeItem implements Action {
  readonly type = ActionTypes.SetActiveContentTreeItem;
  constructor(public payload: { contentTreeItemId: string }) { }
}

export class CreatePageContainer implements Action {
  readonly type = ActionTypes.CreatePageContainer;
  // pageContainerId => page container id of created page container inserted in
  constructor(public payload: { pageContainerId: string; pageContainerData?: any }) { }
}

export class UpdatePageContainerProperties implements Action {
  readonly type = ActionTypes.UpdatePageContainerProperties;
  constructor(public payload: UpdateContainerPropertiesDto) { }
}

export class DeletePageContainer implements Action {
  readonly type = ActionTypes.DeletePageContainer;
  constructor(public payload: { pageContainerId: string; removeStrategy: ContainerRemoveStrategy }) { }
}

export class MoveContentTreeItem implements Action {
  readonly type = ActionTypes.MoveContentTreeItem;
  constructor(public payload: {
    targetPageContainer: ContentTreeItem;
    targetContentTreeItem: ContentTreeItem;
    targetIndex: number;
  }) { }
}

export class UpdatePageCss implements Action {
  readonly type = ActionTypes.UpdatePageCss;
  constructor(public payload: { pageId: string; css: string }) { }
}

export class UpdatePageCssSuccesful implements Action {
  readonly type = ActionTypes.UpdatePageCssSuccesful;
  constructor(public payload: { pageId: string; css: string }) { }
}

export class UpdateContentTreeOfPage implements Action {
  readonly type = ActionTypes.UpdateContentTreeOfPage;
}

export class UpdateContentTreeOfPageSuccessful implements Action {
  readonly type = ActionTypes.UpdateContentTreeOfPageSuccessful;
  constructor(public payload: { contentTree: any }) { }
}


// This action is used to save content tree without any modification, to save it from the code editor on the page
export class UpdateContentTreeAsIs implements Action {
  readonly type = ActionTypes.UpdateContentTreeAsIs;
  constructor(public payload: { pageId: string; contentTree: any }) { }
}

export class UpdateContentTreeAsIsSuccessful implements Action {
  readonly type = ActionTypes.UpdateContentTreeAsIsSuccessful;
  constructor(public payload: { contentTree: any }) { }
}

export class CopyPage implements Action {
  readonly type = ActionTypes.CopyPage;
  constructor(public payload: { targetPageId: string; sourcePageId: string }) { }
}

export class CopyContainer implements Action {
  readonly type = ActionTypes.CopyContainer;
  constructor(public payload: { targetPageId: string; sourcePageId: string; componentPairs?: Array<ComponentPairDto>; sourceContainerId?: string; targetContainerId?: string; components?: any[] }) { }
}

export class CopyContainerSuccessful implements Action {
  readonly type = ActionTypes.CopyContainerSuccessful;
}

export class CopyPageSuccessful implements Action {
  readonly type = ActionTypes.CopyPageSuccessful;
  constructor(public payload: { page: Page }) { }
}

export class CopyTemplateToPageContainer implements Action {
  readonly type = ActionTypes.CopyTemplateToPageContainer;
  constructor(public payload: { templateTree: any[]; targetPageId: string; targetContainerId: string }) { }
}

export class CopyTemplateToPageContainerSuccessful implements Action {
  readonly type = ActionTypes.CopyTemplateToPageContainerSuccessful;
  constructor(public payload: { contentTree: ContentTree }) { }
}

export class CopyActiveContentTemplateToLocalStorage implements Action {
  readonly type = ActionTypes.CopyActiveContentTemplateToLocalStorage;
}

/**
 * We want to hold local storage item in state for subscribe to changes
 *
 * @export
 * @class WriteCopiedTemplateTreeFromLocalStorageToState
 * @implements {Action}
 */
export class WriteCopiedTemplateTreeFromLocalStorageToState implements Action {
  readonly type = ActionTypes.WriteCopiedTemplateTreeFromLocalStorageToState;
  constructor(public payload: ContentEditorClipboardData) { }
}

export class WriteCopiedTemplateTreeFromLocalStorageToStateFailure implements Action {
  readonly type = ActionTypes.WriteCopiedTemplateTreeFromLocalStorageToStateFailure;
  constructor(public payload: { error: any; key: string; timestamp: number }) { }
}

export class PasteTemplateFromLocalStorage implements Action {
  readonly type = ActionTypes.PasteTemplateFromLocalStorage;
}

export class CreateContainerVisibilityCondition implements Action {
  readonly type = ActionTypes.CreateContainerVisibilityCondition;
  constructor(public payload: { pageId: string; body: AddConditionToContainerRequestDto }) { }
}

export class CreateContainerVisibilityConditionSuccessful implements Action {
  readonly type = ActionTypes.CreateContainerVisibilityConditionSuccessful;
  constructor(public payload: { pageContainerId: string; createdVisibilityCondition: any }) { }
}

export class UpdateContainerVisibilityCondition implements Action {
  readonly type = ActionTypes.UpdateContainerVisibilityCondition;
  constructor(public payload: { pageId: string; body: UpdateContainerConditionRequestDto }) { }
}

export class UpdateContainerVisibilityConditionSuccessful implements Action {
  readonly type = ActionTypes.UpdateContainerVisibilityConditionSuccessful;
  constructor(public payload: { pageContainerId: string; updatedVisibilityCondition: any }) { }
}

export class DeleteContainerVisibilityCondition implements Action {
  readonly type = ActionTypes.DeleteContainerVisibilityCondition;
  constructor(public payload: { pageId: string; pageContainerId: string }) { }
}

export class DeleteContainerVisibilityConditionSuccessful implements Action {
  readonly type = ActionTypes.DeleteContainerVisibilityConditionSuccessful;
  constructor(public payload: { pageContainerId: string }) { }
}

export class CreateComponentOutputEvent implements Action {
  readonly type = ActionTypes.CreateComponentOutputEvent;
  constructor(public payload: { componentId: string; createdOutputEvent: ComponentOutputEvent }) { }
}

export class CreateComponentOutputEventSuccessful implements Action {
  readonly type = ActionTypes.CreateComponentOutputEventSuccessful;
  constructor(public payload: { componentId: string; createdOutputEvent: ComponentOutputEvent }) { }
}

export class UpdateComponentOutputEvent implements Action {
  readonly type = ActionTypes.UpdateComponentOutputEvent;
  constructor(public payload: { componentId: string; updatedOutputEventId: string; updatedOutputEvent: ComponentOutputEventPartial }) { }
}

export class UpdateComponentOutputEventSuccessful implements Action {
  readonly type = ActionTypes.UpdateComponentOutputEventSuccessful;
  constructor(public payload: { componentId: string; updatedOutputEventId: string; updatedOutputEvent: ComponentOutputEventPartial }) { }
}

export class DeleteComponentOutputEvent implements Action {
  readonly type = ActionTypes.DeleteComponentOutputEvent;
  constructor(public payload: { componentId: string; eventId: string }) { }
}

export class DeleteComponentOutputEventSuccessful implements Action {
  readonly type = ActionTypes.DeleteComponentOutputEventSuccessful;
  constructor(public payload: { componentId: string; eventId: string }) { }
}


export class SyncPageDataOptimistic implements Action {
  readonly type = ActionTypes.SyncPageDataOptimistic;
  constructor(public payload: { pageData: PagePartial }) { }
}

export class DeletePageComponents implements Action {
  readonly type = ActionTypes.DeletePageComponents;
  constructor(public payload: { componentIds: string[]; pageId: string }) { }
}

export class DeletePageComponentsSuccessful implements Action {
  readonly type = ActionTypes.DeletePageComponentsSuccessful;
  constructor(public payload: { removedComponentIds: string[]; pageId: string }) { }
}

export class ResetContentEditorData implements Action {
  readonly type = ActionTypes.ResetContentEditorData;
}

export class ContentEditorDestroy implements Action {
  readonly type = ActionTypes.ContentEditorDestroy;
  constructor(public payload: { pageId: string; contentTree: ContentTree; lastSavedContentTree: ContentTree; targetPageId?: string }) { }
}

export class GetTemplateById implements Action {
  readonly type = ActionTypes.GetTemplateById;
  constructor(public payload: { id: string }) { }
}

export class GetTemplateByIdSuccessful implements Action {
  readonly type = ActionTypes.GetTemplateByIdSuccessful;
  constructor(public payload: { template: Page }) { }
}

export class AddComponentWithPosition implements Action {
  readonly type = ActionTypes.AddComponentWithPosition;
  constructor(public payload: { component: any; container: ContentTreeItem; index: number }) { }
}

export class GenerateSubscriptionsAndValuesByAi implements Action {
  readonly type = ActionTypes.GenerateSubscriptionsAndValuesByAi;
  constructor(public payload: { pageId: string }) { }
}

export class GenerateSubscriptionsAndValuesByAiSuccessful implements Action {
  readonly type = ActionTypes.GenerateSubscriptionsAndValuesByAiSuccessful;
  constructor(public payload: { pageId: string }) { }
}

export class GenerateComponentIntoPageContainer implements Action {
  readonly type = ActionTypes.GenerateComponentIntoPageContainer;
  constructor(public payload: {
    description: string;
    contentTreeItemId: string;
    pageId: string;
    files?: File[];
  }) {
    console.log('GenerateComponentIntoPageContainer', payload);
  }
}

export class GenerateComponentIntoPageContainerComponentGenerated implements Action {
  readonly type = ActionTypes.GenerateComponentIntoPageContainerComponentGenerated;
  constructor(public payload: { response: any; pageId?: string; pageContainerId?: string }) { }
}
export class GenerateComponentIntoPageContainerSuccessful implements Action {
  readonly type = ActionTypes.GenerateComponentIntoPageContainerSuccessful;
  constructor(public payload: { response: any; pageId?: string; pageContainerId?: string }) { }
}

export class VerifyStatusOfAIGeneratedContainers implements Action {
  readonly type = ActionTypes.VerifyStatusOfAIGeneratedContainers;
  constructor(public payload: { pageId?: string }) { }
}

export class CopyPageContentTree implements Action {
  readonly type = ActionTypes.CopyPageContentTree;
  constructor(public payload: {
    targetPageId: string;
    contentTree: any[];
    targetContainerId?: string;
  }) { }
}

export class CopyPageContentTreeSuccessful implements Action {
  readonly type = ActionTypes.CopyPageContentTreeSuccessful;
  constructor(public payload: { contentTree: any[] }) { }
}

export class GetPageByIdAndReplaceSuccessful implements Action {
  readonly type = ActionTypes.GetPageByIdAndReplaceSuccessful;
  constructor(public payload: { page: Page }) { }
}

export type Actions =
  | ContentEditorError
  | LoadPageWithComponents
  | ReloadPageContent
  | LoadPageWithComponentsSuccessful
  | CreateComponent
  | CreateComponentSuccessful
  | UpdateComponent
  | UpdateComponentSuccessful
  | DeleteComponent
  | DeleteComponentSuccessful
  | UpdateComponentInputs
  | SetActiveContentTreeItem
  | CreatePageContainer
  | UpdatePageContainerProperties
  | DeletePageContainer
  | MoveContentTreeItem
  | UpdatePageCss
  | UpdatePageCssSuccesful
  | CopyPage
  | CopyContainer
  | CopyContainerSuccessful
  | CopyPageSuccessful
  | CopyTemplateToPageContainer
  | CopyTemplateToPageContainerSuccessful
  | CopyActiveContentTemplateToLocalStorage
  | WriteCopiedTemplateTreeFromLocalStorageToState
  | WriteCopiedTemplateTreeFromLocalStorageToStateFailure
  | PasteTemplateFromLocalStorage
  | ResetContentEditorData
  | UpdateContentTreeOfPage
  | UpdateContentTreeOfPageSuccessful
  | UpdateContentTreeAsIs
  | UpdateContentTreeAsIsSuccessful
  | CreateContainerVisibilityCondition
  | CreateContainerVisibilityConditionSuccessful
  | UpdateContainerVisibilityCondition
  | UpdateContainerVisibilityConditionSuccessful
  | DeleteContainerVisibilityCondition
  | DeleteContainerVisibilityConditionSuccessful
  | CreateComponentOutputEvent
  | CreateComponentOutputEventSuccessful
  | UpdateComponentOutputEvent
  | UpdateComponentOutputEventSuccessful
  | DeleteComponentOutputEvent
  | DeleteComponentOutputEventSuccessful
  | SyncPageDataOptimistic
  | DeletePageComponents
  | DeletePageComponentsSuccessful
  | ContentEditorDestroy
  | GetTemplateById
  | GetTemplateByIdSuccessful
  | AddComponentWithPosition
  | GenerateSubscriptionsAndValuesByAi
  | GenerateSubscriptionsAndValuesByAiSuccessful
  | CopyPageContentTree
  | CopyPageContentTreeSuccessful
  | GetPageByIdAndReplaceSuccessful
  ;
